import { Link, createFileRoute } from "@tanstack/react-router";
import { FolderX } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotFoundError, mergePages, useFile, useFileList } from "@/api/queries";
import { AuthGate } from "@/components/AuthGate";
import { useAuth } from "@/auth/stub";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { DropZone } from "@/components/DropZone";
import { FileList } from "@/components/FileList";
import { LoadMore } from "@/components/LoadMore";
import { NewFolderDialog } from "@/components/NewFolderDialog";
import { UploadButton } from "@/components/UploadButton";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/folder/$folderId")({
  component: function FolderView() {
    return (
      <AuthGate>
        <FolderContent />
      </AuthGate>
    );
  },
});

function FolderContent() {
  const { t } = useTranslation();
  const { folderId } = Route.useParams();
  const folder = useFile(folderId);
  // Someone else's folder is reached through a share: its path starts
  // at the shared folder, under "Shared with me".
  const selfId = useAuth((st) => st.user?.id);
  const listing = useFileList(folderId);
  const items = useMemo(() => mergePages(listing.data?.pages), [listing.data]);

  if (
    folder.error instanceof NotFoundError ||
    listing.error instanceof NotFoundError
  ) {
    return (
      <div className="mx-auto mt-16 flex max-w-md flex-col items-center gap-3 text-center">
        <FolderX className="h-10 w-10 text-muted-foreground" aria-hidden />
        <h1 className="text-lg font-semibold">
          {t("files.folder_gone_title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("files.folder_gone_body")}
        </p>
        <Button asChild size="sm">
          <Link to="/">{t("files.go_my_files")}</Link>
        </Button>
      </div>
    );
  }

  // Mutations (upload, rename, move, delete, new folder) invalidate
  // the ["files"] query family themselves; no manual refetch wiring.
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Breadcrumbs
          path={folder.data?.path ?? null}
          shared={!!folder.data && folder.data.owner_id !== selfId}
        />
        <div className="flex items-center gap-2">
          <NewFolderDialog parentId={folderId} />
          <UploadButton parentId={folderId} />
        </div>
      </div>
      {folder.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {folder.error instanceof Error
            ? folder.error.message
            : t("files.error")}
        </p>
      ) : listing.isPending ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t("files.loading")}
        </p>
      ) : listing.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {listing.error instanceof Error
            ? listing.error.message
            : t("files.error")}
        </p>
      ) : (
        <DropZone parentId={folderId}>
          <FileList items={items} />
          <LoadMore
            hasNextPage={listing.hasNextPage}
            isFetchingNextPage={listing.isFetchingNextPage}
            onLoadMore={() => void listing.fetchNextPage()}
          />
        </DropZone>
      )}
    </div>
  );
}
